import { Button, Dialog, DialogBody, DialogFooter } from '@blueprintjs/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ApplicationDataFormValues } from '../../ApplicationDataForm/ApplicationDataFormSchema';
import { ApplicationDataCurrencyField } from '../../Fields/ApplicationDataCurrencyField';
import { ApplicationDataSelectField } from '../../Fields/ApplicationDataSelectField';
import styles from './NewDebtItemDialog.module.css';
import { MarketCountryCode } from '@axo/shared/types';
import { ApplicationDataTextField } from '../../Fields/ApplicationDataTextField';
import { ApplicationConditionalWrapper } from '../../../ConditionalWrappers/ApplicationConditionalWrapper';
import { useDebtTypeOptions } from './options/useDebtTypeOptions';
import { useApplicationConditionalContext } from '../../../ConditionalWrappers/useApplicationConditionalContext';
import { ConditionalWrapper } from '../../../ConditionalWrappers/ConditionalWrapper';
import { DebtType } from '@axo/shared/data-access/types';

interface NewDebtItemDialogProps {
  coApplicantCustomerID: string | undefined;
  coApplicantPersonID: string | undefined;
  applicationID: string;
}

export const NetDebtItemDialog: React.FC<NewDebtItemDialogProps> = ({
  coApplicantCustomerID,
  coApplicantPersonID,
  applicationID,
}) => {
  const applicationConditionalContext = useApplicationConditionalContext();
  const isCoApplicant = coApplicantCustomerID || coApplicantPersonID;
  const fieldArrayName: 'CoDebts' | 'Debts' = isCoApplicant
    ? 'CoDebts'
    : 'Debts';

  const created = useRef(false);
  const [closed, setIsClosed] = useState(false);
  const [index, setIndex] = useState<undefined | number>();

  const debtTypeOptions = useDebtTypeOptions();

  const { watch } = useFormContext<ApplicationDataFormValues>();
  const { fields, insert, remove } = useFieldArray<ApplicationDataFormValues>({
    name: fieldArrayName,
  });

  const type = watch(`${fieldArrayName}.${index ?? 0}.Type`);

  const handleOnClose = useCallback(() => {
    if (created.current === false) {
      remove(index);
    }

    setIsClosed(true);
  }, [index, remove]);

  useEffect(() => {
    if (index !== undefined) return;

    insert(0, {
      Type: null,
      ID: null,
      Creditor: null,
      Debtor: null,
      MonthlyDebtPayment: null,
      NominalInterestRate: null,
      OutstandingAmount: 0,
      RefinanceAmount: null,
      Source: null,
    });

    setIndex(0);
  }, [fields.length, index, insert]);

  if (index === undefined) return null;

  const ctx = { ...applicationConditionalContext, itemType: type };

  return (
    <Dialog isOpen={!closed} onClose={handleOnClose}>
      <DialogBody className={styles.dialogBody}>
        <ApplicationDataSelectField
          applicationID={applicationID}
          name={`${fieldArrayName}.${index}.Type`}
          label="Type"
          options={debtTypeOptions}
        />
        <ApplicationDataCurrencyField
          applicationID={applicationID}
          name={`${fieldArrayName}.${index}.OutstandingAmount`}
          label="Outstanding amount"
        />
        <ApplicationDataCurrencyField
          applicationID={applicationID}
          name={`${fieldArrayName}.${index}.MonthlyDebtPayment`}
          label="Monthly pay"
        />
        <ConditionalWrapper
          context={ctx}
          blacklist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
              itemType: [
                DebtType.Car,
                DebtType.Collection,
                DebtType.Housing,
                DebtType.Student,
                null,
              ],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name={`${fieldArrayName}.${index}.RefinanceAmount`}
            label="Refinance amount"
          />
        </ConditionalWrapper>
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Finland,
                MarketCountryCode.Denmark,
              ],
            },
          ]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name={`${fieldArrayName}.${index}.Creditor`}
            label="Creditor"
          />
        </ApplicationConditionalWrapper>
      </DialogBody>
      <DialogFooter className={styles.dialogFooter}>
        <Button onClick={handleOnClose}>Discard</Button>
        <Button
          disabled={!type}
          intent="primary"
          onClick={() => {
            created.current = true;
            handleOnClose();
          }}
        >
          Create
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
